"use client";

import type { PageSection } from "@/lib/api";
import { useLanguage } from "@/components/LanguageProvider";
import { HeroSection } from "./HeroSection";
import { TextSection } from "./TextSection";
import { FeatureGridSection } from "./FeatureGridSection";
import { CTASection } from "./CTASection";
import { FAQBlockSection } from "./FAQBlockSection";
import { ImageTextRowSection } from "./ImageTextRowSection";
import { ImageSection } from "./ImageSection";
import { CardsSection } from "./CardsSection";
import { VideoSection } from "./VideoSection";
import { MaintenanceSection } from "./MaintenanceSection";
import { HeroThemed } from "@/components/hero/HeroThemed";
import { resolveHeroTheme } from "@/components/hero/types";
import {
  normalizeCardsSectionData,
  normalizeCTASectionData,
  normalizeFAQSectionData,
  normalizeFeatureGridSectionData,
  normalizeHeroData,
  normalizeImageSectionData,
  normalizeImageTextRowData,
  normalizeTextSectionData,
  normalizeVideoSectionData,
} from "@/lib/section-data";

export function SectionRenderer({ section }: { section: PageSection }) {
  const { lang } = useLanguage();
  const data = section.data as Record<string, unknown>;
  const dataAr = section.dataAr as Record<string, unknown> | undefined;

  switch (section.kind) {
    case "hero": {
      const hero = normalizeHeroData(data, dataAr, lang);
      const bgType = String(hero.background_type || "image").toLowerCase();
      const hasVideoUrl = Boolean(hero.video_url?.trim());
      const hasImageBg = Boolean(hero.bg_image_url?.trim());
      const isVideoBg = bgType === "video" && hasVideoUrl;

      if (isVideoBg) {
        return <HeroSection data={{ ...hero, background_type: "video" }} />;
      }
      if (hasImageBg) {
        return <HeroSection data={hero} />;
      }
      return (
        <HeroThemed
          theme={resolveHeroTheme(hero.theme)}
          content={{
            badge: hero.badge_text,
            title: hero.title ?? "",
            subtitle: hero.subtitle,
            primaryCta:
              hero.cta_primary_label && hero.cta_primary_url
                ? { label: hero.cta_primary_label, href: hero.cta_primary_url }
                : undefined,
            secondaryCta:
              hero.cta_secondary_label && hero.cta_secondary_url
                ? { label: hero.cta_secondary_label, href: hero.cta_secondary_url }
                : undefined,
          }}
        />
      );
    }
    case "text":
      return <TextSection data={normalizeTextSectionData(data, dataAr, lang)} />;
    case "feature_grid":
      return (
        <FeatureGridSection data={normalizeFeatureGridSectionData(data, dataAr, lang)} />
      );
    case "cta":
      return <CTASection data={normalizeCTASectionData(data, dataAr, lang)} />;
    case "faq_block":
      return <FAQBlockSection data={normalizeFAQSectionData(data, dataAr, lang)} />;
    case "image_text_row":
      return (
        <ImageTextRowSection
          data={normalizeImageTextRowData(data, dataAr, lang)}
        />
      );
    case "image":
      return (
        <ImageSection data={normalizeImageSectionData(data, dataAr, lang)} />
      );
    case "cards":
      return (
        <CardsSection data={normalizeCardsSectionData(data, dataAr, lang)} />
      );
    case "video":
      return (
        <VideoSection data={normalizeVideoSectionData(data, dataAr, lang)} />
      );
    case "maintenance":
      return <MaintenanceSection section={section} />;
    default:
      return null;
  }
}
